import type { MouseEvent, ReactNode } from 'react';
import { useEffect, useId, useRef } from 'react';
import { cycleTabFocus } from '../utils/focus-trap';
import './ConfirmModal.css';

interface ConfirmModalProps {
  isOpen: boolean;
  onClose: () => void;
  onConfirm: () => void;
  title: string;
  message: string;
  confirmText?: string;
  cancelText?: string;
  icon?: ReactNode;
  /** Extra content (e.g. an optional input) rendered between message and actions. */
  children?: ReactNode;
  /**
   * True while the confirm action is in flight. Marks the Confirm button busy
   * and makes it refuse a second press, so the same action can't be submitted
   * twice.
   *
   * It carries `aria-disabled` rather than `disabled`, and the reason is focus:
   * see the comment on the button itself.
   *
   * Dismissal (Cancel, Escape, backdrop) stays live on purpose: a request that
   * never settles - dropped connection, suspended tab - would otherwise leave
   * the user with no way out of the modal short of reloading the page.
   */
  busy?: boolean;
}

export const ConfirmModal = ({
  isOpen,
  onClose,
  onConfirm,
  title,
  message,
  confirmText = 'Confirm',
  cancelText = 'Cancel',
  icon,
  children,
  busy = false,
}: ConfirmModalProps) => {
  const titleId = useId();
  const dialogRef = useRef<HTMLDivElement>(null);
  // Tracks whether a click actually started on the backdrop, so releasing a
  // text-selection drag outside the dialog doesn't discard the user's input.
  const backdropMouseDownRef = useRef(false);

  // Escape to close, plus a focus trap so Tab can't reach the page behind an
  // aria-modal dialog.
  useEffect(() => {
    if (!isOpen) return;

    const handleKeyDown = (e: KeyboardEvent) => {
      const dialog = dialogRef.current;
      if (!dialog) return;

      if (e.key === 'Escape') {
        // While this dialog is open it owns Escape. Stopping the event here (in
        // the capture phase, before anything else sees it) keeps an ancestor's
        // own Escape handler from firing too - e.g. ProspectViewerPanel would
        // otherwise close the whole panel along with this confirm.
        e.stopPropagation();
        onClose();
        return;
      }

      if (e.key !== 'Tab') return;

      cycleTabFocus(dialog, e);
    };

    document.addEventListener('keydown', handleKeyDown, true);
    return () => document.removeEventListener('keydown', handleKeyDown, true);
  }, [isOpen, onClose]);

  // Move focus into the dialog on open, and restore it on close
  useEffect(() => {
    if (!isOpen) return;

    const previouslyFocused = document.activeElement;
    dialogRef.current?.focus();

    return () => {
      if (previouslyFocused instanceof HTMLElement && previouslyFocused.isConnected) {
        previouslyFocused.focus();
      }
    };
  }, [isOpen]);

  // Lock background scrolling, matching the dashboard panels. Unlike those, the
  // previous value is restored rather than hard-set to "unset" - a confirm
  // nested inside an already-locked panel must not unlock the page on close.
  useEffect(() => {
    if (!isOpen) return;

    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';

    return () => {
      document.body.style.overflow = previousOverflow;
    };
  }, [isOpen]);

  if (!isOpen) return null;

  const handleBackdropMouseDown = (e: MouseEvent<HTMLDivElement>) => {
    backdropMouseDownRef.current = e.target === e.currentTarget;
  };

  const handleBackdropClick = (e: MouseEvent<HTMLDivElement>) => {
    if (e.target !== e.currentTarget) return;

    // The overlay covers the viewport, so a click that lands on it belongs to
    // this modal and must not reach DOM ancestors either. RosterUploadModal's
    // own overlay closes on any click, and would otherwise re-open the very
    // confirm this click just dismissed.
    e.stopPropagation();

    if (!backdropMouseDownRef.current) return;
    backdropMouseDownRef.current = false;
    onClose();
  };

  return (
    <div
      className="confirm-modal-overlay"
      onMouseDown={handleBackdropMouseDown}
      onClick={handleBackdropClick}
    >
      {/*
        stopPropagation is load-bearing: this modal is not portaled, so without
        it a click inside the dialog bubbles on to whatever ancestors happen to
        be above it in the DOM. RosterUploadModal renders its overlay (which
        closes on any click) around this one, and "No, Continue" would re-open
        the very confirm it just dismissed.
      */}
      <div
        className="confirm-modal"
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        ref={dialogRef}
        tabIndex={-1}
        onClick={(e) => e.stopPropagation()}
      >
        {/*
          Everything above the actions scrolls together. The icon and title go inside it rather than
          staying pinned so a tall form gets the whole dialog to scroll through; only Confirm and
          Cancel are held, since those are what a dialog running off the screen made unreachable.
        */}
        <div className="confirm-modal__body">
          {icon && <div className="confirm-modal__icon">{icon}</div>}
          <h2 className="confirm-modal__title" id={titleId}>{title}</h2>
          <p className="confirm-modal__message">{message}</p>
          {children && <div className="confirm-modal__content">{children}</div>}
        </div>
        <div className="confirm-modal__actions">
          <button
            className="confirm-modal__button confirm-modal__button--cancel"
            onClick={onClose}
          >
            {cancelText}
          </button>
          {/*
            `aria-disabled`, not `disabled`. This is the button the user just
            activated, and a disabled button leaves the tab order on the spot -
            so the synchronous state change in the caller's click handler blurs
            it and drops focus to <body>, which is outside an `aria-modal`
            dialog and stays there until the next Tab.

            The success paths close the dialog and restore focus themselves, so
            they hid this. The failure paths leave the dialog open with the
            reason on it, and those are the ones that stranded a keyboard user.

            The handler stays attached and returns early instead, which is the
            same protection against a double submit. PendingInvitations and
            TrustedContactRow do this too, for the same reason.
          */}
          <button
            className="confirm-modal__button confirm-modal__button--confirm"
            onClick={() => {
              if (busy) return;
              onConfirm();
            }}
            aria-disabled={busy}
            aria-busy={busy}
          >
            {confirmText}
          </button>
        </div>
      </div>
    </div>
  );
};
